/**
 * Ícones de clima ilustrados (SVG), coloridos — usados pelo widget de clima
 * do player E pelo preview do editor (admin tem cópia idêntica deste arquivo).
 *
 * Fonte única de arte: `weatherIconSvg(condition)` devolve a STRING do SVG.
 * - Player (DOM): renderiza via <img src={svgToDataUri(...)} />.
 * - Admin (Konva): carrega o mesmo data-URI num Image → Konva.Image.
 *
 * Mantém os 8 códigos canônicos do WeatherData.icon:
 *   clear | partly | cloudy | rain | storm | snow | fog | wind
 */

const SUN = '#FFB300';
const SUN_CORE = '#FFD54F';
const CLOUD = '#ECEFF1';
const CLOUD_SHADE = '#CFD8DC';
const RAIN = '#4FC3F7';
const SNOW = '#E1F5FE';
const BOLT = '#FFCA28';
const FOG = '#B0BEC5';

/** Gera os raios de sol (8 linhas) ao redor de um centro. */
function rays(
  cx: number,
  cy: number,
  ri: number,
  ro: number,
  color: string,
): string {
  const dirs = [
    [1, 0],
    [0.7071, 0.7071],
    [0, 1],
    [-0.7071, 0.7071],
    [-1, 0],
    [-0.7071, -0.7071],
    [0, -1],
    [0.7071, -0.7071],
  ];
  return dirs
    .map(([dx, dy]) => {
      const x1 = (cx + ri * dx).toFixed(1);
      const y1 = (cy + ri * dy).toFixed(1);
      const x2 = (cx + ro * dx).toFixed(1);
      const y2 = (cy + ro * dy).toFixed(1);
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="3.4" stroke-linecap="round"/>`;
    })
    .join('');
}

function sun(cx: number, cy: number, r: number): string {
  return (
    `${rays(cx, cy, r + 3.5, r + 9, SUN)}` +
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${SUN}"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${(r * 0.66).toFixed(1)}" fill="${SUN_CORE}"/>`
  );
}

/** Nuvem (blob) centrada aproximadamente em (cx,cy), escala s. */
function cloud(
  cx: number,
  cy: number,
  s: number,
  fill = CLOUD,
  shade = CLOUD_SHADE,
): string {
  const e = (dx: number, dy: number, rx: number, ry: number, f: string) =>
    `<ellipse cx="${(cx + dx * s).toFixed(1)}" cy="${(cy + dy * s).toFixed(1)}" rx="${(rx * s).toFixed(1)}" ry="${(ry * s).toFixed(1)}" fill="${f}"/>`;
  return (
    e(0, 7, 21, 12, shade) +
    e(-10, 3, 11, 11, fill) +
    e(3, -3, 14, 14, fill) +
    e(13, 4, 10, 10, fill) +
    e(0, 5, 19, 10, fill)
  );
}

function drops(color: string): string {
  return [10, 26, 42]
    .map((x, i) => {
      const x1 = 22 + i * 9;
      return `<line x1="${x1}" y1="48" x2="${x1 - 3}" y2="58" stroke="${color}" stroke-width="3.6" stroke-linecap="round"/>`;
    })
    .join('');
}

function svgWrap(body: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${body}</svg>`;
}

export function weatherIconSvg(condition?: string | null): string {
  const c = (condition || 'clear').toLowerCase();
  switch (c) {
    case 'clear':
      return svgWrap(sun(32, 30, 13));
    case 'partly':
      return svgWrap(sun(22, 22, 9) + cloud(36, 36, 1.0));
    case 'cloudy':
      return svgWrap(cloud(32, 30, 1.15));
    case 'rain':
      return svgWrap(cloud(32, 26, 1.05) + drops(RAIN));
    case 'storm':
      return svgWrap(
        cloud(32, 26, 1.05) +
          `<polygon points="32,42 26,54 31,54 28,62 40,49 34,49 38,42" fill="${BOLT}" stroke="#F9A825" stroke-width="0.6"/>`,
      );
    case 'snow':
      return svgWrap(
        cloud(32, 26, 1.05) +
          [20, 32, 44]
            .map(
              (x) =>
                `<circle cx="${x}" cy="${x === 32 ? 56 : 52}" r="2.6" fill="${SNOW}"/>`,
            )
            .join(''),
      );
    case 'fog':
      return svgWrap(
        cloud(32, 24, 1.0) +
          [46, 52, 58]
            .map(
              (y) =>
                `<line x1="14" y1="${y}" x2="50" y2="${y}" stroke="${FOG}" stroke-width="3.4" stroke-linecap="round"/>`,
            )
            .join(''),
      );
    case 'wind':
      return svgWrap(
        [24, 34, 44]
          .map(
            (y, i) =>
              `<path d="M12 ${y} h${26 + i * 4} a5 5 0 1 0 -5 -5" fill="none" stroke="${FOG}" stroke-width="3.4" stroke-linecap="round"/>`,
          )
          .join(''),
      );
    default:
      return svgWrap(sun(32, 30, 13));
  }
}

export function svgToDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export function weatherIconDataUri(condition?: string | null): string {
  return svgToDataUri(weatherIconSvg(condition));
}
